import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { getCsat, saveCsatSettings, type CsatOverview } from '../../lib/csat-api';
import styles from './admin.module.css';

const pct = (n: number | null) => (n === null ? '—' : `${Math.round(n * 100)}%`);
const DAY_OPTIONS = [7, 30, 90, 365];

// Customer satisfaction (CSAT): turn the post-conversation survey on, set the
// question, and see how it is going — response rate, average, the 1–5
// distribution and the latest comments.
export function CsatSection({ siteId }: { siteId: string }) {
  const [data, setData] = useState<CsatOverview | null>(null);
  const [days, setDays] = useState(30);
  const [enabled, setEnabled] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [askComment, setAskComment] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (d: number) => {
      try {
        const o = await getCsat(siteId, d);
        setData(o);
        setEnabled(o.settings.enabled);
        setPrompt(o.settings.prompt ?? '');
        setAskComment(o.settings.askComment);
        setError(null);
      } catch {
        setError('Could not load CSAT.');
      }
    },
    [siteId],
  );

  useEffect(() => {
    void load(days);
  }, [load, days]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await saveCsatSettings(siteId, { enabled, prompt: prompt.trim() || null, askComment });
      setNotice('Saved.');
      setError(null);
    } catch {
      setError('Could not save the settings.');
    } finally {
      setBusy(false);
    }
  }

  const s = data?.summary;
  const max = s ? Math.max(1, ...s.distribution) : 1;

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Customer satisfaction</p>
      <h2 className={styles.title}>CSAT survey</h2>
      <p className={styles.hint} style={{ marginTop: 0 }}>
        When a conversation ends after an agent or the AI has replied, the customer is asked to rate it 1–5. On the
        website it appears in the chat; on Instagram and API channels it arrives as a message (they can reply with a
        number); a visitor who has left is emailed if their address is known. Links need <code>PUBLIC_SERVER_URL</code> or{' '}
        <code>PUBLIC_APP_URL</code> to be set.
      </p>
      {error && <p className={styles.error} role="alert">{error}</p>}
      {notice && <p className={styles.hint}>{notice}</p>}

      <form className={styles.form} onSubmit={save}>
        <label className={`${styles.field} ${styles.fieldCheck}`}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className={styles.hint} style={{ color: 'var(--inic-ink)' }}>Send a satisfaction survey when a conversation ends</span>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Question</span>
          <input
            className={styles.input}
            maxLength={300}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="How satisfied were you with the support you received?"
          />
        </label>
        <label className={`${styles.field} ${styles.fieldCheck}`}>
          <input type="checkbox" checked={askComment} onChange={(e) => setAskComment(e.target.checked)} />
          <span className={styles.hint} style={{ color: 'var(--inic-ink)' }}>Also ask for a comment</span>
        </label>
        <div className={styles.formActions}>
          <button type="submit" className={styles.button} disabled={busy}>Save</button>
        </div>
      </form>

      {s && (
        <>
          <div style={{ display: 'flex', gap: 'var(--inic-space-3)', alignItems: 'center', margin: 'var(--inic-space-4) 0 var(--inic-space-2)' }}>
            <h3 className={styles.label} style={{ margin: 0 }}>Results</h3>
            <select className={styles.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Period">
              {DAY_OPTIONS.map((d) => (
                <option key={d} value={d}>Last {d} days</option>
              ))}
            </select>
          </div>
          <div className={styles.healthSummary}>
            <span><strong>{s.sent}</strong> sent</span>
            <span><strong>{s.answered}</strong> answered ({pct(s.responseRate)})</span>
            <span>Average <strong>{s.average ?? '—'}</strong> / 5</span>
            <span><strong>{pct(s.satisfied)}</strong> satisfied (4–5)</span>
          </div>
          {s.answered === 0 ? (
            <p className={styles.hint}>No responses in this period yet.</p>
          ) : (
            <div role="img" aria-label="Rating distribution" style={{ display: 'grid', gap: 4, maxWidth: 360 }}>
              {[5, 4, 3, 2, 1].map((n) => {
                const count = s.distribution[n - 1] ?? 0;
                return (
                  <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--inic-size-sm)' }}>
                    <span style={{ width: 28 }}>{n} ★</span>
                    <span style={{ flex: 1, background: 'var(--inic-paper-recessed)', height: 10 }}>
                      <span style={{ display: 'block', height: '100%', width: `${(count / max) * 100}%`, background: 'var(--inic-accent)' }} />
                    </span>
                    <span style={{ width: 28, textAlign: 'right' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          )}

          {data && data.recent.length > 0 && (
            <div className={styles.tableWrap} style={{ marginTop: 'var(--inic-space-3)' }}>
              <table className={styles.table}>
                <thead><tr><th>When</th><th>Rating</th><th>Comment</th><th>Channel</th><th /></tr></thead>
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.sessionId}>
                      <td style={{ whiteSpace: 'nowrap' }}>{r.answeredAt ? new Date(r.answeredAt).toLocaleString() : '—'}</td>
                      <td>{r.rating} ★</td>
                      <td style={{ maxWidth: 360 }}>{r.comment ?? '—'}</td>
                      <td><span className={styles.badgeMuted}>{r.channel}</span></td>
                      <td><Link to={`/conversation/${r.sessionId}`}>Open</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
